import { useState, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSpreadsheet,
  MapPin,
  Building,
  Eye,
  ArrowRight,
  RefreshCw,
  Home,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBulkHomestays = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  // State
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [extractGoogle, setExtractGoogle] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  // Download sample template
  const downloadTemplate = async (format) => {
    try {
      window.open(`${API_URL}/api/homestays/bulk-upload/sample?format=${format}`, '_blank');
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // Handle file selection and preview
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.csv') && !filename.endsWith('.xlsx') && !filename.endsWith('.xls')) {
      toast.error("Please upload a CSV or Excel (.xlsx) file");
      return;
    }

    setSelectedFile(file);
    setUploading(true);
    setPreviewData(null);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/homestays/bulk-upload/preview`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreviewData(response.data);
      toast.success(`Preview generated: ${response.data.stats.total} rows found`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse file");
      setSelectedFile(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Import homestays
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setImporting(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        `${API_URL}/api/homestays/bulk-upload/import?extract_google=${extractGoogle}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setImportResult(response.data.results);
      setPreviewData(null);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Reset all state
  const handleReset = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get status badge for preview row
  const getStatusBadge = (status) => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Valid
          </span>
        );
      case 'invalid':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Invalid
          </span>
        );
      case 'duplicate':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Duplicate
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-bulk-homestays">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">
              Bulk Upload Homestays
            </h1>
            <p className="text-muted-foreground">
              Import multiple homestays from CSV or Excel files with Google Maps data extraction
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadTemplate('csv')} data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              CSV Template
            </Button>
            <Button variant="outline" onClick={() => downloadTemplate('xlsx')} data-testid="download-xlsx-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel Template
            </Button>
          </div>
        </div>

        {/* Instructions Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="font-semibold text-blue-900 mb-3">How to use Bulk Upload</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div className="flex items-start gap-2">
              <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <div>
                <p className="font-medium">Download Template</p>
                <p className="text-blue-700">Get the CSV or Excel template with sample data</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <div>
                <p className="font-medium">Fill Your Data</p>
                <p className="text-blue-700">Add Google Maps URLs to auto-extract property data</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <div>
                <p className="font-medium">Preview & Import</p>
                <p className="text-blue-700">Review validation results before importing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {!previewData && !importResult && (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h3 className="font-semibold text-lg mb-4">Upload File</h3>
            
            {/* Options */}
            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
              <Checkbox
                id="extract-google"
                checked={extractGoogle}
                onCheckedChange={setExtractGoogle}
              />
              <label htmlFor="extract-google" className="text-sm font-medium cursor-pointer">
                Auto-extract data from Google Maps URLs (recommended)
              </label>
            </div>

            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-primary transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="bulk-upload"
                data-testid="bulk-upload-input"
              />
              <label htmlFor="bulk-upload" className="cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-12 h-12 animate-spin text-primary" />
                    <p className="text-muted-foreground">Analyzing file...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-12 h-12 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-lg text-foreground">
                        Click to upload CSV or Excel file
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        or drag and drop your file here
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported formats: .csv, .xlsx
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>
        )}

        {/* Preview Section */}
        {previewData && (
          <div className="space-y-4">
            {/* Stats Summary */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Preview Results</h3>
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Upload Different File
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-foreground">{previewData.stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-green-600">{previewData.stats.valid}</p>
                  <p className="text-sm text-green-700">Ready to Import</p>
                </div>
                <div className="p-4 bg-red-50 rounded-xl text-center">
                  <XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-red-600">{previewData.stats.invalid}</p>
                  <p className="text-sm text-red-700">Invalid</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-xl text-center">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-yellow-600">{previewData.stats.duplicate}</p>
                  <p className="text-sm text-yellow-700">Duplicates</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <MapPin className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-3xl font-bold text-blue-600">{previewData.stats.google_extraction_pending}</p>
                  <p className="text-sm text-blue-700">Google Extraction</p>
                </div>
              </div>

              {/* Google Extraction Option */}
              {previewData.stats.google_extraction_pending > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                  <Checkbox
                    id="extract-google-preview"
                    checked={extractGoogle}
                    onCheckedChange={setExtractGoogle}
                  />
                  <label htmlFor="extract-google-preview" className="text-sm text-blue-800 cursor-pointer">
                    <span className="font-medium">Extract Google data during import</span>
                    <span className="text-blue-600 ml-1">
                      ({previewData.stats.google_extraction_pending} rows have Google URLs/Place IDs)
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Detailed Preview Table */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="p-4 border-b">
                <h4 className="font-semibold">Row Details</h4>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Row</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Location</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Village</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Price</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Google</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Issues</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewData.preview.map((row, idx) => (
                      <tr key={idx} className={row.status === 'valid' ? 'bg-white' : row.status === 'duplicate' ? 'bg-yellow-50/50' : 'bg-red-50/50'}>
                        <td className="px-4 py-3 text-gray-500">{row.row_number}</td>
                        <td className="px-4 py-3">{getStatusBadge(row.status)}</td>
                        <td className="px-4 py-3 font-medium">{row.original_data.name || '-'}</td>
                        <td className="px-4 py-3">
                          {row.location_matched ? (
                            <span className="inline-flex items-center text-green-700">
                              <MapPin className="w-3 h-3 mr-1" />
                              {row.location_matched}
                            </span>
                          ) : row.original_data.location_name ? (
                            <span className="text-yellow-600">{row.original_data.location_name} (not found)</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.village_matched ? (
                            <span className="inline-flex items-center text-green-700">
                              <Building className="w-3 h-3 mr-1" />
                              {row.village_matched}
                            </span>
                          ) : row.original_data.village_name ? (
                            <span className="text-yellow-600">{row.original_data.village_name} (not found)</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.processed_data.price_per_night ? (
                            <span>₹{row.processed_data.price_per_night.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.google_extraction_needed ? (
                            <span className="inline-flex items-center text-blue-600">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.errors.length > 0 ? (
                            <ul className="text-red-600 text-xs space-y-1">
                              {row.errors.map((err, i) => <li key={i}>• {err}</li>)}
                            </ul>
                          ) : row.warnings.length > 0 ? (
                            <ul className="text-yellow-600 text-xs space-y-1">
                              {row.warnings.map((warn, i) => <li key={i}>• {warn}</li>)}
                            </ul>
                          ) : (
                            <span className="text-green-600 text-xs">✓ Ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Import Button */}
            {previewData.stats.valid > 0 && (
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleReset}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Import {previewData.stats.valid} Homestays
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Import Results */}
        {importResult && (
          <div className="bg-white rounded-xl shadow-soft p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Import Complete
              </h3>
              <Button onClick={handleReset}>
                <Upload className="w-4 h-4 mr-2" />
                Upload More
              </Button>
            </div>

            {/* Results Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-600">{importResult.created}</p>
                <p className="text-sm text-green-700">Created</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-blue-600">{importResult.google_extracted}</p>
                <p className="text-sm text-blue-700">Google Enriched</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-yellow-600">{importResult.duplicates}</p>
                <p className="text-sm text-yellow-700">Duplicates Rejected</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-gray-600">{importResult.skipped}</p>
                <p className="text-sm text-gray-700">Skipped</p>
              </div>
            </div>

            {/* Created Homestays */}
            {importResult.created_homestays?.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Created Homestays:</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                  {importResult.created_homestays.map((h, idx) => (
                    <div key={idx} className="p-3 bg-green-50 rounded-lg flex items-start gap-2">
                      <Home className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{h.name}</p>
                        <p className="text-xs text-gray-600">
                          {h.location && <span>{h.location}</span>}
                          {h.village && <span> / {h.village}</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {importResult.errors?.length > 0 && (
              <div className="p-4 bg-red-50 rounded-xl">
                <h4 className="font-medium text-red-800 mb-2">Errors ({importResult.errors.length}):</h4>
                <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBulkHomestays;
